<main class="modern-page holding-page no-hero-page">
  <section class="intro-card holdings-intro">
    <div class="agent-mark" aria-hidden="true">
      <span class="agent-core">AI</span>
      <span class="agent-node node-top"></span>
      <span class="agent-node node-left"></span>
      <span class="agent-node node-right"></span>
    </div>
    <span class="section-kicker">Holdings · Agent Infrastructure</span>
    <h1>Agent Server for Coordinating Intelligent Transportation and Commercial Real Estate</h1>
    <p class="brief-info">ANYSPACE's agent server is the coordination layer for smart assets that use software and AI technologies across transportation and commercial real estate. It connects operational systems, shared context, and specialized agents so assets can be monitored, analyzed, and coordinated from a common intelligence layer.</p>
  </section>

  <section class="strategy-section content" aria-labelledby="architecture-heading">
    <div class="section-heading">
      <span></span>
      <h2 id="architecture-heading">Smart Asset Coordination Architecture</h2>
      <span></span>
    </div>

    <div class="architecture-grid">
      <article class="system-card">
        <div class="system-icon" aria-hidden="true">T</div>
        <span class="card-kicker">Connected Domain</span>
        <h3>Transportation Software</h3>
        <p>Connect transportation operations and smart mobility assets to a shared agent layer for operational context and coordinated workflows.</p>
        <ul class="check-list compact">
          <li>Fleet and asset signals</li>
          <li>Routing and operational workflows</li>
          <li>Telematics and system events</li>
          <li>Maintenance and exception context</li>
        </ul>
      </article>

      <article class="system-card agent-server-card">
        <div class="server-status"><span></span> Coordination Layer</div>
        <div class="server-visual" aria-hidden="true">
          <div class="server-rack"><span></span><span></span><span></span></div>
          <div class="server-ai">AI</div>
        </div>
        <span class="card-kicker">Core Infrastructure</span>
        <h3>Agent Server</h3>
        <p>Provide a common orchestration point where specialized agents can receive context, coordinate tasks, and interact with authorized software and smart-asset systems.</p>
        <div class="capability-row">
          <span>Context</span>
          <span>Agents</span>
          <span>Workflows</span>
          <span>Actions</span>
        </div>
      </article>

      <article class="system-card">
        <div class="system-icon" aria-hidden="true">CRE</div>
        <span class="card-kicker">Connected Domain</span>
        <h3>Commercial Real Estate</h3>
        <p>Connect building and property software to the same coordination layer so physical assets and operating systems can participate in intelligent workflows.</p>
        <ul class="check-list compact">
          <li>Building and property systems</li>
          <li>IoT and operational signals</li>
          <li>Energy and maintenance context</li>
          <li>Access and facility workflows</li>
        </ul>
      </article>
    </div>

    <div class="flow-band" aria-label="Agent server coordination flow">
      <span>Transportation</span>
      <strong>↔</strong>
      <span class="flow-core">Agent Server</span>
      <strong>↔</strong>
      <span>Commercial Real Estate</span>
    </div>
  </section>

  <section class="strategy-section content" aria-labelledby="role-heading">
    <div class="section-heading">
      <span></span>
      <h2 id="role-heading">Role of the Agent Server</h2>
      <span></span>
    </div>

    <div class="feature-grid two-column">
      <article class="feature-card">
        <div class="card-icon" aria-hidden="true">01</div>
        <div>
          <h3>Integrate</h3>
          <p>Connect transportation software, commercial real estate systems, smart assets, and approved data sources through a consistent coordination layer.</p>
        </div>
      </article>

      <article class="feature-card">
        <div class="card-icon" aria-hidden="true">02</div>
        <div>
          <h3>Understand Context</h3>
          <p>Give agents the operational context needed to interpret asset state, system events, requests, and cross-domain conditions.</p>
        </div>
      </article>

      <article class="feature-card">
        <div class="card-icon" aria-hidden="true">03</div>
        <div>
          <h3>Coordinate</h3>
          <p>Route work among specialized agents and connected systems so transportation and property operations can participate in shared workflows.</p>
        </div>
      </article>

      <article class="feature-card">
        <div class="card-icon" aria-hidden="true">04</div>
        <div>
          <h3>Execute Authorized Actions</h3>
          <p>Provide a controlled path for agents to trigger permitted software actions while keeping system boundaries and operational responsibilities explicit.</p>
        </div>
      </article>
    </div>
  </section>

  <section class="operating-model" aria-labelledby="model-heading">
    <span class="section-kicker">Operating Model</span>
    <h2 id="model-heading">Software + AI + Smart Assets</h2>
    <p class="model-intro">The agent server is not the asset itself. It is part of the intelligence and coordination fabric that allows specialized software, AI agents, and connected physical assets to work together.</p>

    <div class="numbered-grid">
      <article class="mini-card">
        <span>1</span>
        <h3>Observe</h3>
        <p>Connected systems provide operational state, events, and relevant asset context.</p>
      </article>
      <article class="mini-card">
        <span>2</span>
        <h3>Interpret</h3>
        <p>Agents use available context to understand the request, event, or operating condition.</p>
      </article>
      <article class="mini-card">
        <span>3</span>
        <h3>Coordinate</h3>
        <p>The server connects the appropriate agent capabilities, software services, and workflows.</p>
      </article>
      <article class="mini-card">
        <span>4</span>
        <h3>Act</h3>
        <p>Authorized actions are sent to connected systems, with results returned into the operating context.</p>
      </article>
    </div>
  </section>

  <section class="contact-strip">
    <span class="section-kicker">Agent Infrastructure</span>
    <h2>Discuss transportation, commercial real estate, or agent-server integration</h2>
    <p>Share the smart assets, software systems, or operating workflows you want to connect and we will create a tracked inquiry for follow-up.</p>
    <a routerLink="/contact" [queryParams]="{ source: 'holdings-agent-server', type: 'innovation' }" class="modern-cta">Contact for Agent Server Information</a>
  </section>
</main>
